import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Divider,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import { Close, Upload, Download } from "@mui/icons-material";
import { useGeoFileConverterViewModel } from "./GeoFileConverter.vm";
import { INPUT_FILE_EXTENSIONS, OUTPUT_FORMATS } from "./GeoFileConverter.types";

export const GeoFileConverter: React.FC = () => {
	const vm = useGeoFileConverterViewModel();

	if (!vm.isSuperUser) {
		return (
			<Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: "md", margin: "auto" }}>
				<Alert severity="error">You do not have permission to access this page.</Alert>
			</Box>
		);
	}

	return (
		<Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: "md", margin: "auto" }}>
			<Typography variant="h5" gutterBottom>
				GeoFile Converter
			</Typography>

			<Card variant="outlined">
				<CardContent>
					{/* Upload section */}
					<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
						<input
							ref={vm.fileInputRef}
							type="file"
							accept={INPUT_FILE_EXTENSIONS}
							multiple
							style={{ display: "none" }}
							onChange={(e) => vm.handleFileSelect(e.target.files)}
						/>
						<Button variant="contained" startIcon={<Upload />} onClick={vm.handleUploadClick}>
							Upload Files
						</Button>
						{vm.loadedFiles.length > 0 && (
							<Button variant="outlined" onClick={vm.handleClear}>
								Clear
							</Button>
						)}
					</Stack>
					<Typography variant="caption" color="text.secondary">
						Supported: .zip, .shp, .geojson, .json, .kml, .kmz
					</Typography>

					{/* Loading indicator */}
					{vm.status === "loading" && (
						<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
							<CircularProgress size={20} />
							<Typography variant="body2">Loading files...</Typography>
						</Stack>
					)}

					{/* Error message */}
					{vm.errorMessage && (
						<Alert severity="error" sx={{ mt: 2 }}>
							{vm.errorMessage}
						</Alert>
					)}

					{/* Success message */}
					{vm.status === "success" && (
						<Alert severity="success" sx={{ mt: 2 }}>
							Conversion complete!
						</Alert>
					)}

					{/* Loaded files list */}
					{vm.loadedFiles.length > 0 && (
						<>
							<Divider sx={{ my: 2 }} />
							<Typography variant="subtitle2" sx={{ mb: 1 }}>
								Loaded Files
							</Typography>
							<Stack spacing={1}>
								{vm.loadedFiles.map((loaded, index) => (
									<Box
										key={index}
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											p: 1,
											borderRadius: 1,
											bgcolor: "grey.50",
										}}
									>
										<Box>
											<Typography variant="body2" fontWeight="medium">
												{loaded.file.name}
											</Typography>
											<Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
												<Chip label={loaded.detectedFormat} size="small" color="primary" variant="outlined" />
												<Chip label={`${loaded.featureCount} features`} size="small" variant="outlined" />
												{loaded.geometryTypes.map((type) => (
													<Chip key={type} label={type} size="small" variant="outlined" />
												))}
											</Stack>
										</Box>
										<IconButton size="small" onClick={() => vm.handleRemoveFile(index)}>
											<Close fontSize="small" />
										</IconButton>
									</Box>
								))}
							</Stack>

							{/* Convert section */}
							<Divider sx={{ my: 2 }} />
							<Typography variant="subtitle2" sx={{ mb: 1 }}>
								Convert To
							</Typography>
							<Stack direction="row" spacing={2} alignItems="center">
								<FormControl size="small" sx={{ minWidth: 160 }}>
									<InputLabel>Target Format</InputLabel>
									<Select
										value={vm.targetFormat}
										label="Target Format"
										onChange={(e) => vm.handleFormatChange(e.target.value as any)}
									>
										{OUTPUT_FORMATS.map((format) => (
											<MenuItem key={format} value={format}>
												{format}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<Button
									variant="contained"
									color="success"
									startIcon={vm.status === "converting" ? <CircularProgress size={18} color="inherit" /> : <Download />}
									onClick={vm.handleConvert}
									disabled={vm.status === "converting" || vm.status === "loading"}
								>
									{vm.status === "converting" ? "Converting..." : "Convert & Download"}
								</Button>
							</Stack>
						</>
					)}
				</CardContent>
			</Card>
		</Box>
	);
};
